<template>
  <v-card flat>
    <v-card-text class="py-6">
      <form ref="ppUIForm" v-if="pickupPointsUIForm.length > 0">
        <v-timeline align-top dense>
          <v-timeline-item
            small
            v-for="(pp, ppIndex) in pickupPointsUIForm"
            :color="pickupMarkerColors[ppIndex]"
            :key="pp.name"
            style="min-height: 120px"
          >
            <v-row class="pt-1 void pb-2">
              <v-col cols="2" class="void">
                <strong>{{ pp.name }}</strong>
              </v-col>
              <v-col cols="8" class="void pr-2">
                <span
                  v-if="!pp.edit"
                  :class="[
                    'h5',
                    'text-uppercase',
                    !pp.outletName && 'font-italic',
                  ]"
                >
                  {{ pp.outletName || "Отсутствуют склады" }}
                </span>
                <v-combobox
                  v-if="pp.edit"
                  :items="outletItems"
                  item-text="name"
                  item-value="id"
                  label="Склады"
                  multiple
                  chips
                  hide-details
                  :loading="loadingPPQueue.includes(pp.name)"
                  loader-height="4"
                  :value="
                    pp.outlets
                      ? pp.outlets.map((outletId) =>
                          outletItems.find((item) => item.id === outletId)
                        )
                      : []
                  "
                  @change="
                    (newOutlets) => {
                      onSelectedOutlet(ppIndex, newOutlets);
                    }
                  "
                ></v-combobox>
              </v-col>
              <v-col cols="2" class="void">
                <v-row class="void">
                  <template v-if="!pp.edit">
                    <v-btn icon @click="enableEdit(ppIndex)">
                      <v-icon size="20">mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <template v-if="pp.edit">
                    <v-btn
                      icon
                      :disabled="loadingPPQueue.includes(pp.name)"
                      @click="onUpdatePickupPoint(ppIndex, pp.name)"
                    >
                      <v-icon size="20">mdi-check</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      :disabled="loadingPPQueue.includes(pp.name)"
                      @click="disableEdit(ppIndex)"
                    >
                      <v-icon size="20">mdi-close</v-icon>
                    </v-btn>
                  </template>
                </v-row>
              </v-col>
            </v-row>
          </v-timeline-item>
        </v-timeline>
      </form>
      <v-info-board
        v-else
        displayIcon="mdi-store-marker"
        message="Склады не обнаружены"
      ></v-info-board>
    </v-card-text>
  </v-card>
</template>

<script>
import { notificatonStatuses } from "@/enumerations";
import { mapFields } from "vuex-map-fields";
import { mapGetters } from "vuex";
import VInfoBoard from "@/components/info-board/VInfoBoard";
import FormatMixin from "@/mixins/Format";
import HashGenerator from "@/helpers/HashGenerator";
import ServiceFacade from "@/services/ServiceFacade";

export default {
  name: "PickupPointsPopupContent",
  mixins: [FormatMixin],
  components: {
    VInfoBoard,
  },
  data() {
    return {
      pickupMarkerColors: [
        "teal lighten-3",
        "purple lighten-1",
        "purple lighten-5",
        "purple darken-4",
        "purple accent-1",
        "deep-purple lighten-5",
        "light-blue darken-1",
        "blue lighten-1",
        "blue darken-2",
        "blue-grey lighten-3",
        "lime lighten-1",
        "lime darken-1",
        "lime lighten-4",
        "lime accent-4",
        "green lighten-1",
        "light-green darken-1",
        "light-green lighten-2",
        "amber accent-1",
        "amber accent-4",
        "amber lighten-1",
        "orange lighten-1",
        "orange lighten-3",
        "orange lighten-5",
        "yellow lighten-1",
        "yellow lighten-3",
        "yellow accent-4",
        "orange accent-2",
        "deep-orange lighten-3",
        "deep-orange lighten-5",
        "grey lighten-1",
        "indigo darken-2",
        "indigo lighten-5",
        "pink accent-3",
        "pink lighten-4",
        "red lighten-1",
        "red lighten-4",
        "red accent-3",
        "red accent-4",
        "red darken-3",
        "brown lighten-3",
        "cyan lighten-1",
        "cyan lighten-3",
        "cyan darken-2",
      ],
      loadingPPQueue: [],
    };
  },
  computed: {
    ...mapFields("profile", ["pickupPointsUIForm"]),
    ...mapGetters("profile", {
      company: "company",
    }),
    outletItems() {
      return this.company.outlets || [];
    },
  },
  methods: {
    recoverLastOutletsChanges(ppIndex, cipher) {
      this.pickupPointsUIForm[ppIndex].outlets = JSON.parse(
        HashGenerator.decrypt("outletHashCommit", cipher)
      );
    },
    saveLastOutletsChanges(ppIndex, data) {
      this.pickupPointsUIForm[ppIndex].lastOutletsCommit =
        HashGenerator.encrypt("outletHashCommit", data);
    },
    disableEdit(ppIndex) {
      this.pickupPointsUIForm[ppIndex].edit = false;
    },
    enableEdit(ppIndex) {
      this.recoverLastOutletsChanges(
        ppIndex,
        this.pickupPointsUIForm[ppIndex].lastOutletsCommit
      );
      this.pickupPointsUIForm[ppIndex].edit = true;
      for (let index = 0; index < this.pickupPointsUIForm.length; index++) {
        if (index !== ppIndex) {
          this.pickupPointsUIForm[index].edit = false;
        }
      }
    },
    async onUpdatePickupPoint(ppIndex, ppName) {
      try {
        this.loadingPPQueue.push(ppName);
        const { name, outlets } = this.pickupPointsUIForm[ppIndex];

        await ServiceFacade.updateKaspiPP({
          pp: { name, outlets },
        });

        this.pickupPointsUIForm[ppIndex].outletName = this.outletItems
          .filter((o) => outlets.includes(o.id))
          .map((o) => o.name)
          .join(", ");
        this.saveLastOutletsChanges(ppIndex, JSON.stringify(outlets));

        this.disableEdit(ppIndex);
      } catch (error) {
        this.$store.commit("core/SET_NOTIFICATION", {
          status: notificatonStatuses.error,
          message:
            error.message || "Ошибка при обновлении складов с точками доставок",
        });
      } finally {
        this.loadingPPQueue = this.loadingPPQueue.filter(
          (pid) => pid !== ppName
        );
      }
    },
    onSelectedOutlet(ppIndex, newOutlets) {
      this.pickupPointsUIForm[ppIndex].outlets = newOutlets.map((o) => o.id);
    },
    shuffleColors() {
      for (let i = this.pickupMarkerColors.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = this.pickupMarkerColors[i];
        this.pickupMarkerColors[i] = this.pickupMarkerColors[j];
        this.pickupMarkerColors[j] = temp;
      }
    },
    getRandomPickupMarkerColor() {
      const colors = Array.from(this.pickupMarkerColors);
      const color = colors[Math.floor(Math.random() * colors.length)];
      return color;
    },
  },
  created() {
    this.shuffleColors();
  },
};
</script>
